import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Gift, Award, Package, Clock, CreditCard, CheckCircle, ChevronRight, Clipboard, RefreshCw } from 'lucide-react';
import { OrderDetailModal } from '../../../core/components/dashboard/Common';
import {
  ORDER_STATUS,
  STATUS_LABELS,
  STATUS_COLORS,
} from '../../../core/constants/orderStatus';
import useAuthStore from '../../../core/stores/authStore';
import useOrderStore from '../../../core/stores/orderStore';
import styles from './RealtimeDashboard.module.css';

/**
 * 실시간 현황 대시보드
 */
const RealtimeDashboard = () => {
  const navigate = useNavigate();
  const pharmacy = useAuthStore(state => state.pharmacy);

  // orderStore에서 구독 (MainLayout이 폴링)
  const orders = useOrderStore(state => state.orders);
  const lastUpdate = useOrderStore(state => state.lastUpdate) || new Date();
  const isLoading = useOrderStore(state => state.isLoading);

  const [selectedOrder, setSelectedOrder] = useState(null);

  // KPI 계산
  const kpi = useMemo(() => {
    const counts = {
      totalToday: orders.length,
      totalAmount: 0,
      paidCount: 0,
      paidAmount: 0,
      pending: 0,
      paid: 0,
      completed: 0,
      pickedUp: 0,
    };

    orders.forEach(order => {
      const amount = order.total_amount || 0;
      counts.totalAmount += amount;

      switch (order.status) {
        case ORDER_STATUS.PENDING:
          counts.pending++;
          break;
        case ORDER_STATUS.PAID:
          counts.paid++;
          counts.paidCount++;
          counts.paidAmount += amount;
          break;
        case ORDER_STATUS.COMPLETED:
          counts.completed++;
          counts.paidCount++;
          counts.paidAmount += amount;
          break;
        case ORDER_STATUS.PICKED_UP:
          counts.pickedUp++;
          counts.paidCount++;
          counts.paidAmount += amount;
          break;
        default:
          break;
      }
    });

    return counts;
  }, [orders]);

  // 최근 주문 5개
  const recentOrders = useMemo(() => orders.slice(0, 5), [orders]);

  // 날짜 포맷
  const formattedDate = useMemo(() => {
    const d = lastUpdate;
    return `${d.getMonth() + 1}월 ${d.getDate()}일 ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')} 기준`;
  }, [lastUpdate]);

  const handleRefresh = () => {
    // MainLayout의 pollOrders를 직접 호출할 수는 없으므로 window 이벤트로 트리거
    window.dispatchEvent(new CustomEvent('refreshOrders'));
  };

  const handleOrderClick = order => {
    setSelectedOrder(order);
  };

  const handleCloseModal = () => {
    setSelectedOrder(null);
  };

  const handleOrderUpdate = updatedOrder => {
    window.dispatchEvent(new CustomEvent('refreshOrders'));
    setSelectedOrder(updatedOrder);
  };

  return (
      <div className={styles.pageContainer}>
        {/* ===== Top Summary Bar ===== */}
        <div className={styles.topSummaryBar}>
          <div className={styles.summaryDateBlock}>
            <span className={styles.summaryDateLabel}>오늘</span>
            <span className={styles.summaryDateTime}>{formattedDate}</span>
          </div>
          <div className={styles.summaryDivider} />
          <div className={styles.summaryItem}>
            <div className={`${styles.summaryIcon} ${styles.summaryIconOrder}`}>
              <Clipboard size={20} />
            </div>
            <div className={styles.summaryInfo}>
              <span className={styles.summaryLabel}>
                주문 ({kpi.totalToday}건)
              </span>
              <span className={styles.summaryValue}>
                {kpi.totalAmount.toLocaleString()}원
              </span>
            </div>
          </div>
          <div className={styles.summaryDivider} />
          <div className={styles.summaryItem}>
            <div className={`${styles.summaryIcon} ${styles.summaryIconPayment}`}>
              <CreditCard size={20} />
            </div>
            <div className={styles.summaryInfo}>
              <span className={styles.summaryLabel}>
                결제 ({kpi.paidCount}건)
              </span>
              <span className={styles.summaryValue}>
                {kpi.paidAmount.toLocaleString()}원
              </span>
            </div>
          </div>
        </div>

        {/* ===== Summary Cards ===== */}
        <div className={styles.bottomGrid}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardHeader}>
              <span className={styles.summaryCardTitle}>
                주문 요약 <span className={styles.summaryCardSub}>(오늘)</span>
              </span>
              <button
                className={styles.summaryCardRefresh}
                onClick={handleRefresh}
              >
                <RefreshCw size={16} />
              </button>
            </div>
            <div className={styles.summaryCardList}>
              <div className={styles.summaryCardRow}>
                <span className={styles.summaryCardLabel}>승인대기</span>
                <span className={styles.summaryCardValue}>{kpi.pending}</span>
              </div>
              <div className={styles.summaryCardRow}>
                <span className={styles.summaryCardLabel}>결제완료</span>
                <span className={styles.summaryCardValue}>{kpi.paid}</span>
              </div>
              <div className={styles.summaryCardRow}>
                <span className={styles.summaryCardLabel}>조제완료</span>
                <span className={styles.summaryCardValue}>{kpi.completed}</span>
              </div>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <div className={styles.summaryCardHeader}>
              <span className={styles.summaryCardTitle}>픽업 현황</span>
              <button
                className={styles.summaryCardRefresh}
                onClick={handleRefresh}
              >
                <RefreshCw size={16} />
              </button>
            </div>
            <div className={styles.summaryCardList}>
              <div className={styles.summaryCardRow}>
                <span className={styles.summaryCardLabel}>픽업 대기</span>
                <span className={styles.summaryCardValueHighlight}>
                  {kpi.completed}
                </span>
              </div>
              <div className={styles.summaryCardRow}>
                <span className={styles.summaryCardLabel}>픽업 완료</span>
                <span className={styles.summaryCardValue}>{kpi.pickedUp}</span>
              </div>
              <div className={styles.summaryCardRow}>
                <span className={styles.summaryCardLabel}>전체 처리</span>
                <span className={styles.summaryCardValue}>
                  {kpi.completed + kpi.pickedUp}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <div className={styles.summaryCardHeader}>
              <span className={styles.summaryCardTitle}>
                문의현황 <span className={styles.summaryCardSub}>(미처리)</span>
              </span>
              <button
                className={styles.summaryCardRefresh}
                onClick={handleRefresh}
              >
                <RefreshCw size={16} />
              </button>
            </div>
            <div className={styles.summaryCardList}>
              <div className={styles.summaryCardRow}>
                <span className={styles.summaryCardLabel}>처방 문의</span>
                <span className={styles.summaryCardValue}>0</span>
              </div>
              <div className={styles.summaryCardRow}>
                <span className={styles.summaryCardLabel}>결제/배송 문의</span>
                <span className={styles.summaryCardValue}>0</span>
              </div>
              <div className={styles.summaryCardRow}>
                <span className={styles.summaryCardLabel}>긴급 메시지</span>
                <span className={styles.summaryCardValue}>0</span>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Order Status Flow ===== */}
        <div className={styles.statusSection}>
          <div className={styles.statusSectionHeader}>
            <div className={styles.statusHeaderLeft}>
              <span className={styles.statusSectionTitle}>주문현황</span>
              <button className={styles.refreshBtn} onClick={handleRefresh}>
                <RefreshCw size={14} />
                새로고침
              </button>
              <span className={styles.lastUpdateText}>
                {lastUpdate.toLocaleTimeString('ko-KR')} (마지막 업데이트)
                {isLoading && ' 로딩중...'}
              </span>
            </div>
          </div>

          <div className={styles.statusFlow}>
            <div className={styles.statusItem}>
              <div className={`${styles.statusCircle} ${styles.statusPending}`}>
                <Clock size={28} />
              </div>
              <span className={styles.statusName}>승인대기</span>
              <span className={`${styles.statusCount} ${styles.statusCountPending}`}>
                {kpi.pending}
              </span>
            </div>

            <div className={styles.statusArrow}>
              <ChevronRight size={20} />
            </div>

            <div className={styles.statusItem}>
              <div className={`${styles.statusCircle} ${styles.statusPaid}`}>
                <CreditCard size={28} />
              </div>
              <span className={styles.statusName}>결제완료</span>
              <span className={`${styles.statusCount} ${styles.statusCountPaid}`}>
                {kpi.paid}
              </span>
            </div>

            <div className={styles.statusArrow}>
              <ChevronRight size={20} />
            </div>

            <div className={styles.statusItem}>
              <div className={`${styles.statusCircle} ${styles.statusCompleted}`}>
                <Package size={28} />
              </div>
              <span className={styles.statusName}>조제중</span>
              <span className={`${styles.statusCount} ${styles.statusCountCompleted}`}>
                {kpi.completed}
              </span>
            </div>

            <div className={styles.statusArrow}>
              <ChevronRight size={20} />
            </div>

            <div className={styles.statusItem}>
              <div className={`${styles.statusCircle} ${styles.statusPickedUp}`}>
                <CheckCircle size={28} />
              </div>
              <span className={styles.statusName}>픽업완료</span>
              <span className={`${styles.statusCount} ${styles.statusCountPickedUp}`}>
                {kpi.pickedUp}
              </span>
            </div>
          </div>
        </div>

        {/* ===== Banner Cards ===== */}
        <div className={styles.bannerRow}>
          <div
            className={`${styles.bannerCard} ${styles.bannerBlue}`}
            onClick={() => navigate('/dashboard/points/distribute')}
          >
            <div className={styles.bannerContent}>
              <h3 className={styles.bannerTitle}>포인트를 지급해보세요</h3>
              <p className={styles.bannerDesc}>
                고객에게 포인트를 간편하게 지급할 수 있습니다.
              </p>
              <button className={styles.bannerButton}>시작하기</button>
            </div>
            <div className={styles.bannerIconArea}>
              <Award size={72} />
            </div>
          </div>

          <div
            className={`${styles.bannerCard} ${styles.bannerGreen}`}
            onClick={() => navigate('/dashboard/coupons/distribute')}
          >
            <div className={styles.bannerContent}>
              <h3 className={styles.bannerTitle}>
                쿠폰 지급, 간단하게 해결하세요.
              </h3>
              <p className={styles.bannerDesc}>
                고객에게 쿠폰을 지급하여 재방문을 유도합니다.
              </p>
              <button className={styles.bannerButton}>시작하기</button>
            </div>
            <div className={styles.bannerIconArea}>
              <Gift size={72} />
            </div>
          </div>
        </div>

        {/* ===== Recent Orders Table ===== */}
        <div className={styles.historySection}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTitle}>최근 주문</span>
            <span className={styles.sectionCount}>총 {orders.length}건</span>
          </div>

          <div className={styles.tableBox}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>주문번호</th>
                  <th>환자명</th>
                  <th>연락처</th>
                  <th>상태</th>
                  <th>주문시간</th>
                  <th>결제금액</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className={styles.emptyState}>
                      <div className={styles.emptyText}>로딩 중...</div>
                    </td>
                  </tr>
                ) : recentOrders.length > 0 ? (
                  recentOrders.map(order => (
                    <tr
                      key={order.order_id}
                      className={styles.clickableRow}
                      onClick={() => handleOrderClick(order)}
                    >
                      <td className={styles.orderId}>#{order.order_id}</td>
                      <td>{order.user?.name || '-'}</td>
                      <td>{order.user?.phone_number || '-'}</td>
                      <td>
                        <span
                          className={styles.statusBadge}
                          style={{
                            backgroundColor:
                              STATUS_COLORS[order.status] + '20',
                            color: STATUS_COLORS[order.status],
                          }}
                        >
                          {STATUS_LABELS[order.status] || order.status}
                        </span>
                      </td>
                      <td>
                        {new Date(order.created_at).toLocaleString('ko-KR')}
                      </td>
                      <td className={styles.amount}>
                        {order.total_amount
                          ? `${order.total_amount.toLocaleString()}원`
                          : '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className={styles.emptyState}>
                      <FileText className={styles.emptyIcon} />
                      <div className={styles.emptyText}>주문이 없습니다</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Order Detail Modal */}
        {selectedOrder && (
          <OrderDetailModal
            order={selectedOrder}
            onClose={handleCloseModal}
            onUpdate={handleOrderUpdate}
          />
        )}
      </div>
  );
};

export default RealtimeDashboard;
